import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { cityService } from '../services/api';
import { CreateCityModal } from '../components/CityModals';

const STATUS_COLORS = {
  active: { bg: '#e8f5e9', color: '#2e7d32' },
  paused: { bg: '#fff3e0', color: '#e65100' },
  draft: { bg: '#f5f5f5', color: '#757575' },
  seeding: { bg: '#e3f2fd', color: '#1565c0' },
  enriching: { bg: '#e8eaf6', color: '#283593' },
  calibrating: { bg: '#fce4ec', color: '#880e4f' },
};

const CityList = () => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalCount: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [sorting, setSorting] = useState({ sortBy: 'name', sortOrder: 'asc' });
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchCities = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: filters.search,
        sortBy: sorting.sortBy,
        sortOrder: sorting.sortOrder,
      };
      if (filters.status) params.status = filters.status;

      const response = await cityService.getAll(params);
      setCities(response.data || []);
      if (response.pagination) {
        setPagination(prev => ({ ...prev, ...response.pagination }));
      }
    } catch (err) {
      console.error('Error fetching cities:', err);
      setError('Failed to load cities');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters.search, filters.status, sorting.sortBy, sorting.sortOrder]);

  useEffect(() => { fetchCities(); }, [fetchCities]);

  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => { setMessage(null); setError(null); }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  const handleSearch = (e) => {
    setFilters({ ...filters, search: e.target.value });
    setPagination({ ...pagination, page: 1 });
  };

  const handleSort = (field) => {
    setSorting(prev => ({
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIcon = (field) => {
    if (sorting.sortBy !== field) return '↕';
    return sorting.sortOrder === 'asc' ? '↑' : '↓';
  };

  const handleArchive = async (city) => {
    if (!window.confirm(`Pause city "${city.name}"? Polling will stop.`)) return;
    setActionLoading(city.id);
    try {
      await cityService.delete(city.id);
      setMessage(`City "${city.name}" paused`);
      fetchCities();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to archive city');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestore = async (city) => {
    setActionLoading(city.id);
    try {
      await cityService.restore(city.id);
      setMessage(`City "${city.name}" restored`);
      fetchCities();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to restore city');
    } finally {
      setActionLoading(null);
    }
  };

  const formatNumber = (n) => {
    if (n == null) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toLocaleString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const classMap = {
      active: 'status-active',
      paused: 'status-pending',
      draft: 'status-disabled',
      seeding: 'status-pending',
      enriching: 'status-pending',
      calibrating: 'status-pending',
    };
    return (
      <span className={`status-badge ${classMap[status] || 'status-disabled'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>City Management</h1>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          + Add City
        </button>
      </div>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search cities by name..."
            value={filters.search}
            onChange={handleSearch}
          />
        </div>
        <select value={filters.status} onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPagination({ ...pagination, page: 1 }); }}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="draft">Draft</option>
          <option value="seeding">Seeding</option>
          <option value="enriching">Enriching</option>
          <option value="calibrating">Calibrating</option>
        </select>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('name')}>City {getSortIcon('name')}</th>
                  <th className="sortable" onClick={() => handleSort('country_code')}>Country {getSortIcon('country_code')}</th>
                  <th className="sortable" onClick={() => handleSort('status')}>Status {getSortIcon('status')}</th>
                  <th style={{ textAlign: 'right' }}>Hex Grid</th>
                  <th style={{ textAlign: 'right' }}>Road Hexes</th>
                  <th style={{ textAlign: 'right' }}>Sentinels</th>
                  <th>Poll Interval</th>
                  <th>Last Poll</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cities.length === 0 ? (
                  <tr><td colSpan="9" className="empty-state">No cities found. Add a city to get started.</td></tr>
                ) : (
                  cities.map((city) => (
                    <tr key={city.id}>
                      <td className="user-cell">
                        <div className="user-avatar" style={{ background: STATUS_COLORS[city.status]?.bg || '#f5f5f5' }}>
                          {city.name?.charAt(0) || '?'}
                        </div>
                        <div className="user-info">
                          <div className="user-name">
                            <Link to={`/cities/${city.id}`}>{city.name}</Link>
                          </div>
                          <div className="user-email">{city.timezone}</div>
                        </div>
                      </td>
                      <td>{city.countryCode}</td>
                      <td>{getStatusBadge(city.status)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {formatNumber(city.hexCountTotal)}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {formatNumber(city.roadHexCount)}
                        {city.hexCountTotal > 0 && (
                          <span style={{ fontSize: '0.7rem', color: '#888', marginLeft: 4 }}>
                            ({((city.roadHexCount / city.hexCountTotal) * 100).toFixed(0)}%)
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span style={{ fontWeight: 600 }}>{city.activeSentinelCount}</span>
                        <span style={{ fontSize: '0.7rem', color: '#888' }}> / {city.totalRouteCount}</span>
                      </td>
                      <td>{city.pollIntervalMinutes} min</td>
                      <td style={{ fontSize: '0.8rem' }}>{formatDate(city.lastPollAt)}</td>
                      <td>
                        <div className="actions-dropdown">
                          <button className="btn btn-secondary btn-sm">
                            {actionLoading === city.id ? '...' : '⋮'}
                          </button>
                          <div className="dropdown-menu">
                            <Link to={`/cities/${city.id}`} className="dropdown-item">View Details</Link>
                            {city.status === 'paused' ? (
                              <button className="dropdown-item text-success" onClick={() => handleRestore(city)}>Restore</button>
                            ) : (
                              <button className="dropdown-item text-danger" onClick={() => handleArchive(city)}>Pause</button>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.totalCount > 0 && pagination.totalPages > 1 && (
            <div className="pagination">
              <span>Showing {((pagination.page - 1) * pagination.pageSize) + 1}-{Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount} cities</span>
              <div className="pagination-buttons">
                <button disabled={pagination.page === 1} onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}>
                  ← Previous
                </button>
                <button disabled={pagination.page === pagination.totalPages} onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}>
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {showCreateModal && (
        <CreateCityModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={(msg) => { setMessage(msg); setShowCreateModal(false); fetchCities(); }}
          onError={setError}
        />
      )}
    </div>
  );
};

export default CityList;
